import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import KoltLogo from "./KoltLogo";
import { MadeInUSABadge } from "./MadeInUSABadge";
import { COMPANY, LEGAL_LINKS } from "../lib/company";

const FOOTER_LINKS = {
  Services: [
    ["Roll Labels", "/shop/roll-labels"],
    ["Digital Print", "/shop/digital-print"],
    ["Custom Boxes & Packaging", "/shop/marketing-collateral/custom-boxes-packaging"],
    ["Offset Print", "/shop/offset-print"],
    ["Marketing", "/shop/marketing"],
    ["Large Format & Banners", "/shop/large-format"],
    ["Booklets & Books", "/shop/booklets-books"],
    ["Wedding Collateral", "/shop/marketing-collateral/wedding-collateral"],
  ],
  Company: [
    ["About Us", "/about"],
    ["Industries", "/industries"],
    ["Blog", "/blog"],
    ["Customer Reviews", "/reviews"],
    ["Resources", "/resources"],
    ["Foil Color Chart", "/tools/foil-color-chart"],
    ["For Designers & Agencies", "/partners"],
    ["Contact", "/contact"],
  ],
  Account: [
    ["Request a Quote", "/quote"],
    ["My Account", "/dashboard"],
    ["Order History", "/dashboard/orders"],
    ["Saved Quotes", "/dashboard/quotes"],
  ],
  Legal: LEGAL_LINKS,
};

export default function Footer() {
  return (
    <footer
      className="bg-kolt-slate-dark text-white border-t-4 border-kolt-orange mt-24"
      data-testid="site-footer"
    >
      <div className="container-page py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4 space-y-5">
          <KoltLogo className="h-12 w-auto" variant="light" />
          <p className="text-sm text-white/65 leading-relaxed max-w-xs">
            Custom printing in Houston, Texas — business cards, roll labels,
            large-format banners, foil-stamped invitations, presentation
            folders and booklets. Same-day cut-off 11&nbsp;AM CT. Ships
            nationwide.
          </p>
          <address className="space-y-2.5 text-sm not-italic" itemScope itemType="https://schema.org/PostalAddress">
            <div className="flex items-start gap-3 text-white/80">
              <MapPin className="w-4 h-4 text-kolt-orange flex-shrink-0 mt-0.5" />
              <span data-testid="footer-address" className="leading-relaxed">
                <span className="block font-semibold text-white" itemProp="name">{COMPANY.legalName}</span>
                <span itemProp="addressLocality">{COMPANY.city}</span>,{" "}
                <span itemProp="addressRegion">{COMPANY.state}</span>
                <meta itemProp="addressCountry" content={COMPANY.country} />
              </span>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <Phone className="w-4 h-4 text-kolt-orange flex-shrink-0" />
              <a
                href={`tel:${COMPANY.phoneTel}`}
                className="hover:text-kolt-orange"
                itemProp="telephone"
                data-testid="footer-phone"
              >
                {COMPANY.phoneDisplay}
              </a>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <Mail className="w-4 h-4 text-kolt-orange flex-shrink-0" />
              <a
                href="mailto:estimate@koltprinting.com"
                className="hover:text-kolt-orange"
                itemProp="email"
                data-testid="footer-email"
              >
                estimate@koltprinting.com
              </a>
            </div>
          </address>
        </div>

        {Object.entries(FOOTER_LINKS).map(([heading, links]) => (
          <div key={heading} className="md:col-span-2">
            <h4 className="font-mono text-[11px] tracking-[0.28em] font-bold uppercase text-kolt-orange mb-5">
              {heading}
            </h4>
            <ul className="space-y-3 text-sm">
              {links.map(([label, to]) => (
                <li key={label}>
                  <Link to={to} className="text-white font-semibold hover:text-kolt-orange transition-colors" data-testid={`footer-link-${to.replace(/\//g, "-").replace(/^-/, "")}`}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-white/10">
        <div className="container-page py-5 flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-white/40 font-mono uppercase tracking-widest">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} {COMPANY.legalName} · Quality you can see</span>
          <MadeInUSABadge variant="dark" />
          <span data-testid="footer-b2b">B2B &amp; trade printer · Made with industrial precision in Houston, TX</span>
        </div>
      </div>
    </footer>
  );
}
